import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useAuthStore } from './auth';

/**
 * "מישהו אחר התחבר לחשבון שלך" (30/09/2026, בקשת דורון).
 *
 * כניסה ממחשב/דפדפן אחר לוקחת את קו ה-SIP, והשרת מוחק את הטוקן של המכשיר
 * הקודם (SipLineClaimController). הבקשה הבאה כאן מקבלת 401 עם
 * code=session_replaced — ואז: ניתוק הסופטפון, יציאה מהמערכת, והודעה בולטת
 * (SessionReplacedNotice) שנשארת גם אחרי המעבר למסך הכניסה ורענון.
 * שיחה חיה לא נחתכת: היציאה מחכה לסופה, וההודעה אומרת את זה.
 */

export interface ReplacedBy {
  label?: string | null;
  ip?: string | null;
  at?: string | null;
}

interface SessionReplacedState {
  replacedBy: ReplacedBy | null;
  /** המשתמש עדיין בשיחה — היציאה תקרה כשהיא תסתיים. */
  waitingForCall: boolean;
  show: (by: ReplacedBy, waitingForCall: boolean) => void;
  callEnded: () => void;
  dismiss: () => void;
}

export const useSessionReplaced = create<SessionReplacedState>()(
  persist(
    (set) => ({
      replacedBy: null,
      waitingForCall: false,
      show: (replacedBy, waitingForCall) => set({ replacedBy, waitingForCall }),
      callEnded: () => set({ waitingForCall: false }),
      dismiss: () => set({ replacedBy: null, waitingForCall: false }),
    }),
    {
      name: 'gocall-session-replaced',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (s) => ({ replacedBy: s.replacedBy }),
    },
  ),
);

const IN_CALL = ['on-call', 'on-hold', 'ringing-in', 'ringing-out'];
let handling = false;

/** נקרא מה-interceptor על 401 עם code=session_replaced. בטוח לקריאות חוזרות. */
export async function handleSessionReplaced(by: ReplacedBy | undefined): Promise<void> {
  if (handling) return;
  handling = true;

  const { useSipStore } = await import('@/personas/agent/stores/sipStore');
  const logout = () => {
    try { useSipStore.getState().unregister(); } catch { /* כבר מנותק */ }
    useAuthStore.getState().clear();
    useSessionReplaced.getState().callEnded();
    handling = false;
  };

  const inCall = IN_CALL.includes(useSipStore.getState().state);
  useSessionReplaced.getState().show(by ?? {}, inCall);

  if (!inCall) {
    logout();
    return;
  }
  const unsub = useSipStore.subscribe((s) => {
    if (!IN_CALL.includes(s.state)) { unsub(); logout(); }
  });
}
